import React, { useCallback, useEffect, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";

/**
 * BeforeAfterSlider — drag or click to reveal.
 * Props: before (url), after (url), title (string)
 */
export const BeforeAfterSlider = ({ before, after, title }) => {
    const [pos, setPos] = useState(50);
    const ref = useRef(null);
    const dragging = useRef(false);

    const setFromClientX = useCallback((clientX) => {
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const x = Math.max(rect.left, Math.min(clientX, rect.right));
        const pct = ((x - rect.left) / rect.width) * 100;
        setPos(pct);
    }, []);

    useEffect(() => {
        const onMove = (e) => {
            if (!dragging.current) return;
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            setFromClientX(clientX);
        };
        const onUp = () => (dragging.current = false);
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        window.addEventListener("touchmove", onMove, { passive: true });
        window.addEventListener("touchend", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("touchend", onUp);
        };
    }, [setFromClientX]);

    const start = (e) => {
        dragging.current = true;
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        setFromClientX(clientX);
    };

    return (
        <div
            className="ba-slider rounded-2xl aspect-[16/10] bg-hhc-sand"
            ref={ref}
            onMouseDown={start}
            onTouchStart={start}
            data-testid={`before-after-${title?.toLowerCase().replace(/\s+/g, "-") || "slider"}`}
        >
            <img src={after} alt={`${title || "Project"} after`} />
            <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${pos}%` }}
            >
                <img
                    src={before}
                    alt={`${title || "Project"} before`}
                    className="w-full h-full object-cover"
                    style={{
                        width: `${100 / (pos / 100)}%`,
                        maxWidth: "none",
                    }}
                />
            </div>

            {/* Before/After labels */}
            <span className="absolute top-4 left-4 z-10 bg-hhc-ivory/90 backdrop-blur px-3 py-1 rounded-full text-[10px] tracking-[0.24em] uppercase font-semibold text-hhc-ink">
                Before
            </span>
            <span className="absolute top-4 right-4 z-10 bg-hhc-ink/85 backdrop-blur px-3 py-1 rounded-full text-[10px] tracking-[0.24em] uppercase font-semibold text-hhc-ivory">
                After
            </span>

            {/* Handle */}
            <div className="ba-handle-line" style={{ left: `${pos}%` }} />
            <div
                className="ba-handle-knob"
                style={{ left: `${pos}%` }}
                aria-hidden
            >
                <MoveHorizontal size={18} strokeWidth={1.75} />
            </div>
        </div>
    );
};

export default BeforeAfterSlider;
